import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { PageHead, Btn, Card, Field, Toggle, ImageUpload, Label, AiSeoButton, SeoPreview } from "@/admin/ui";
import { toast } from "sonner";
import { Trash2, Plus, GripVertical } from "lucide-react";

const uid = () => Math.random().toString(36).slice(2, 10);
const empty = () => ({ title: "", slug: "", intro: "", hero_image: "", published: false, looks: [], seo: {} });
const emptyLook = () => ({ id: uid(), image: "", caption: "", product_slugs: [] });

const ProductPicker = ({ products, selected, onChange }) => {
  const [q, setQ] = useState("");
  const list = products.filter((p) => (p.name || "").toLowerCase().includes(q.toLowerCase()));
  const toggle = (slug) => onChange(selected.includes(slug) ? selected.filter((s) => s !== slug) : [...selected, slug]);
  return (
    <div>
      <Label>Shoppable pieces ({selected.length})</Label>
      {selected.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mb-2">
          {selected.map((slug) => {
            const p = products.find((x) => x.slug === slug);
            return (
              <span key={slug} className="inline-flex items-center gap-1 text-[0.68rem] bg-espresso text-cream px-2 py-1">
                {p ? p.name : slug}
                <button type="button" onClick={() => toggle(slug)} className="hover:text-gold">×</button>
              </span>
            );
          })}
        </div>
      )}
      <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search pieces…"
        className="w-full bg-ivory border border-espresso/20 px-3 py-2 text-sm outline-none focus:border-espresso mb-2" />
      <div className="max-h-44 overflow-y-auto border border-espresso/10 bg-ivory">
        {list.map((p) => (
          <label key={p.slug} className="flex items-center gap-2 px-3 py-2 text-sm border-b border-espresso/5 cursor-pointer hover:bg-sand/40">
            <input type="checkbox" checked={selected.includes(p.slug)} onChange={() => toggle(p.slug)} />
            <span className="flex-1">{p.name}</span>
            <span className="text-xs text-espresso/40">{p.status}</span>
          </label>
        ))}
        {list.length === 0 && <p className="px-3 py-3 text-xs text-espresso/40">No pieces found.</p>}
      </div>
    </div>
  );
};

const LookbookAdmin = () => {
  const [items, setItems] = useState([]);
  const [products, setProducts] = useState([]);
  const [editing, setEditing] = useState(null);
  const load = () => api.get("/admin/lookbooks").then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => {
    load();
    api.get("/admin/products").then((r) => setProducts(r.data)).catch(() => {});
  }, []);
  const up = (k, v) => setEditing((e) => ({ ...e, [k]: v }));

  const looks = editing?.looks || [];
  const setLooks = (arr) => up("looks", arr);
  const upLook = (id, k, v) => setLooks(looks.map((l) => (l.id === id ? { ...l, [k]: v } : l)));
  const moveLook = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= looks.length) return;
    const arr = [...looks];
    [arr[i], arr[j]] = [arr[j], arr[i]];
    setLooks(arr);
  };

  const save = async () => {
    try {
      if (editing.id) await api.put(`/admin/lookbooks/${editing.id}`, editing);
      else await api.post("/admin/lookbooks", editing);
      toast.success("Saved"); setEditing(null); load();
    } catch { toast.error("Failed"); }
  };
  const del = async (id) => { if (!window.confirm("Delete lookbook?")) return; await api.delete(`/admin/lookbooks/${id}`); load(); };

  if (editing) return (
    <div className="max-w-2xl">
      <PageHead title={editing.id ? "Edit Lookbook" : "New Lookbook"}>
        <Btn variant="ghost" onClick={() => setEditing(null)}>Cancel</Btn>
        <Btn onClick={save} testid="admin-save">Save</Btn>
      </PageHead>
      <Card className="mb-4">
        <Field label="Title" value={editing.title} onChange={(v) => up("title", v)} testid="lookbook-title" />
        <Field label="Slug" value={editing.slug} onChange={(v) => up("slug", v)} placeholder="auto from title" />
        <Toggle label="Published" value={editing.published} onChange={(v) => up("published", v)} testid="lookbook-published" />
        <ImageUpload label="Hero image" value={editing.hero_image} onChange={(v) => up("hero_image", v)} />
        <Field label="Hero film (video URL)" value={editing.hero_film} onChange={(v) => up("hero_film", v)} placeholder="optional — a cinematic film that leads the story" />
        <Field label="Intro" textarea value={editing.intro} onChange={(v) => up("intro", v)} />
      </Card>

      <Card className="mb-4">
        <div className="flex justify-between items-center mb-4">
          <h2 className="font-serif text-2xl">Looks</h2>
          <Btn variant="ghost" onClick={() => setLooks([...looks, emptyLook()])} testid="lookbook-add-look"><Plus size={13} className="inline" /> Look</Btn>
        </div>
        {looks.length === 0 && <p className="text-sm text-espresso/40">No looks yet. Add a look, upload its image, and pick the pieces worn in it.</p>}
        <div className="space-y-6">
          {looks.map((l, i) => (
            <div key={l.id} className="border border-espresso/15 p-4 bg-ivory/50" data-testid={`lookbook-look-${i}`}>
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2 text-espresso/50"><GripVertical size={15} /><span className="text-[0.68rem] tracking-[0.14em] uppercase">Look {i + 1}</span></div>
                <div className="flex items-center gap-2">
                  <button type="button" onClick={() => moveLook(i, -1)} className="text-espresso/50 text-xs px-1">↑</button>
                  <button type="button" onClick={() => moveLook(i, 1)} className="text-espresso/50 text-xs px-1">↓</button>
                  <button type="button" onClick={() => setLooks(looks.filter((x) => x.id !== l.id))} className="text-wine"><Trash2 size={15} /></button>
                </div>
              </div>
              <ImageUpload label="Look image" value={l.image} onChange={(v) => upLook(l.id, "image", v)} testid={`lookbook-look-image-${i}`} />
              <Field label="Caption (optional)" value={l.caption} onChange={(v) => upLook(l.id, "caption", v)} testid={`lookbook-look-caption-${i}`} />
              <ProductPicker products={products} selected={l.product_slugs || []} onChange={(v) => upLook(l.id, "product_slugs", v)} />
            </div>
          ))}
        </div>
      </Card>

      <Card>
        <AiSeoButton subject={editing.title} context={editing.intro} onResult={(d) => up("seo", { ...editing.seo, title: d.title, description: d.description })} testid="lookbook-ai-seo" />
        <Field label="SEO Title" value={editing.seo?.title} onChange={(v) => up("seo", { ...editing.seo, title: v })} />
        <Field label="SEO Description" textarea value={editing.seo?.description} onChange={(v) => up("seo", { ...editing.seo, description: v })} />
        <SeoPreview title={editing.seo?.title} description={editing.seo?.description} path={`/lookbook/${editing.slug || ""}`} />
      </Card>
    </div>
  );

  return (
    <div>
      <PageHead title="Lookbook" sub={`${items.length} lookbooks — shoppable editorial stories`}>
        <Btn onClick={() => setEditing(empty())} testid="admin-new-lookbook">+ New Lookbook</Btn>
      </PageHead>
      <div className="bg-cream border border-espresso/10">
        {items.length === 0 && <p className="px-5 py-6 text-sm text-espresso/40">No lookbooks yet. Create one, then add looks and link the pieces worn in each.</p>}
        {items.map((a) => (
          <div key={a.id} className="flex items-center gap-4 px-5 py-3 border-b border-espresso/5" data-testid={`lookbook-row-${a.slug}`}>
            <div className="flex-1">
              <p className="font-serif text-lg">{a.title}</p>
              <p className="text-xs text-espresso/50">{(a.looks?.length ?? a.look_count ?? 0)} looks · {a.published ? "published" : "draft"}</p>
            </div>
            <Btn variant="ghost" onClick={() => setEditing(a)}>Edit</Btn>
            <button onClick={() => del(a.id)} className="text-wine text-xs tracking-widest uppercase">Delete</button>
          </div>
        ))}
      </div>
    </div>
  );
};

export default LookbookAdmin;
